/** biome-ignore-all lint/a11y/noStaticElementInteractions: fix accessibility props */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: fix accessibility props */
import cx from 'classnames';
import {type FC, type ReactNode, useContext} from 'react';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {isFabricWebview} from '~/common/utils/webview';
import {EventCreateContent} from './components/EventCreateContent';
import {EventCreateNavigation} from './components/EventCreateNavigation';
import type {UpsellButtonsProps} from './components/UpsellButtons';
import {EventCreateBackground} from './EventCreateBackground';
import {EventCreateContext} from './EventCreateContext';
import {useAdsPanel} from './hooks/useAdsPanel';
import {useDesignBackground} from './hooks/useDesignBackground';
import {useDesignCanvas} from './hooks/useDesignCanvas';
import {useDesignValidation} from './hooks/useDesignValidation';
import {useKeyboardControls} from './hooks/useKeyboardControls';
import {useModals} from './hooks/useModals';
import './EventCreateDesign.sass';

interface EventCreateDesignProps {
  children?: ReactNode;
  scrollable?: boolean;
  // biome-ignore lint/style/useNamingConvention: allow invalid naming
  UpsellButtons?: FC<UpsellButtonsProps>;
  showAds?: boolean;
}

export const EventCreateDesign: FC<EventCreateDesignProps> = ({
  children = null,
  scrollable,
  UpsellButtons,
  showAds = true,
}) => {
  const {showBottomNav} = useContext(FabricContext);
  const {canvas} = useContext(EventCreateContext);

  const {currentSection, Section, isDesktop, style, selectedBackground} = useDesignBackground();

  const {designContainerRef, dismissPremiumFeaturesWarning, premiumFeaturesWarning} =
    useDesignCanvas();

  const {adsPanel, isPremium, usedPremiumFeature, AdsUpsellModalComponent} = useAdsPanel();

  const {
    showBackground,
    setAnimatedBgReady,
    isUsingPremiumFeature,
    toggleDowngradeModal,
    onGoPremiumClick,
    isLoading,
    modalsComponents,
  } = useModals();

  useDesignValidation();

  useKeyboardControls(canvas);

  return (
    <div
      className={cx('event-create-design', {
        scrollable,
        'event-create-design--free': !isPremium,
        'lock-scroll': isUsingPremiumFeature,
      })}
      style={style}
      ref={designContainerRef}
    >
      {showAds && adsPanel}

      {!isFabricWebview() && (isPremium || usedPremiumFeature) && (
        <EventCreateBackground
          showBackground={showBackground}
          selectedBackground={selectedBackground}
          setAnimatedBgReady={setAnimatedBgReady}
        />
      )}

      <div
        className={cx({overlay: true, open: premiumFeaturesWarning})}
        onClick={dismissPremiumFeaturesWarning}
        data-qa-id="premium-features-warning-overlay"
        style={{zIndex: 3}}
      />

      <div
        className="event-create-design__container preview-create-design__content"
        id="design-section"
      >
        {isFabricWebview() && (isPremium || usedPremiumFeature) && (
          <EventCreateBackground
            showBackground={showBackground}
            selectedBackground={selectedBackground}
            setAnimatedBgReady={setAnimatedBgReady}
          />
        )}

        <EventCreateContent
          isDesktop={isDesktop}
          Section={Section}
          currentSection={currentSection}
        />

        <EventCreateNavigation
          showBottomNav={showBottomNav}
          currentSection={currentSection}
          isUsingPremiumFeature={isUsingPremiumFeature}
          usedPremiumFeature={usedPremiumFeature}
          isPremium={isPremium}
          toggleDowngradeModal={toggleDowngradeModal}
          handleGoPremiumClick={onGoPremiumClick}
          isLoading={isLoading}
          isDesktop={isDesktop}
          UpsellButtons={UpsellButtons}
        />
      </div>

      {modalsComponents}
      <AdsUpsellModalComponent />
      {children}
    </div>
  );
};
